/**
 * Sheet document — a template filled in for one project: field values,
 * revisions, which floors the per-floor pages repeat for, and per viewport
 * what to show and how it is framed. Stored on the server per project
 * (validated with `parseSheetDoc`) and re-exportable at any time.
 *
 * Viewport settings are keyed by {@link viewportSettingsKey}:
 * `<pageId>/<elementId>` applies to every instance of the page (and is the
 * fallback for per-floor pages, bound to each instance's floor);
 * `<pageId>:<floorId>/<elementId>` overrides one floor's instance.
 */

import type { GradientType } from '../gradients';
import type { PrintColor } from './color';
import type { PrintTemplateElement, ViewportKind } from './spec';
import type { TextSize } from './units';

export const SHEET_DOC_SCHEMA_VERSION = 1;

export interface PrintTemplateRef {
  /** `builtin`: one of the shipped templates (`id` = builtin id); `org`: an organisation template (`id` = its document id). */
  source: 'builtin' | 'org';
  id: string;
  /** The pinned published version, so editing the template never silently changes an issued sheet. */
  version: number;
}

/**
 * Plan layer categories, from the DXF layer suffixes (`<Floor>_<Suffix>`,
 * see `layerCategory` in the client's DXF export). `Labels` covers every
 * `Labels_<Kind>` layer.
 */
export const PLAN_LAYER_CATEGORIES = ['Walls', 'Openings', 'Outlines', 'Objects', 'Dimensions', 'Rooms', 'RoomTags', 'Polylines', 'Polygons', 'Points', 'Beams', 'Columns', 'Labels'] as const;
export type PlanLayerCategory = (typeof PLAN_LAYER_CATEGORIES)[number];

export interface PlanLayerPen {
  visible: boolean;
  weightMm: number;
  /** null = the layer's own color (K-only black when the viewport is monochrome). */
  color: PrintColor | null;
}

/** Defaults follow ISO 128 pen hierarchy: cut walls heaviest, annotation lightest. */
export const DEFAULT_PLAN_LAYER_PENS: Readonly<Record<PlanLayerCategory, PlanLayerPen>> = {
  Walls: { visible: true, weightMm: 0.35, color: null },
  Openings: { visible: true, weightMm: 0.18, color: null },
  Outlines: { visible: true, weightMm: 0.25, color: null },
  Objects: { visible: true, weightMm: 0.13, color: null },
  Dimensions: { visible: true, weightMm: 0.09, color: null },
  Rooms: { visible: false, weightMm: 0.13, color: null },
  RoomTags: { visible: true, weightMm: 0.09, color: null },
  Polylines: { visible: true, weightMm: 0.13, color: null },
  Polygons: { visible: true, weightMm: 0.13, color: null },
  Points: { visible: false, weightMm: 0.13, color: null },
  Beams: { visible: true, weightMm: 0.18, color: null },
  Columns: { visible: true, weightMm: 0.25, color: null },
  Labels: { visible: true, weightMm: 0.09, color: null },
};

/** Thinnest pen a plan line may get: below this, printers drop or blur the line. */
export const MIN_PLAN_PEN_MM = 0.03;

/** Plan text whose PAPER size the user chooses — the "exact font size" of the export. */
export const PLAN_TEXT_CATEGORIES = ['dimensions', 'roomTags', 'labels', 'openings'] as const;
export type PlanTextCategory = (typeof PLAN_TEXT_CATEGORIES)[number];

/** 2.5 mm capitals: the ISO 3098 default for dimensions on A3/A1 plans. */
export const DEFAULT_PLAN_TEXT_SIZES: Readonly<Record<PlanTextCategory, TextSize>> = {
  dimensions: { value: 2.5, unit: 'mm', basis: 'cap' },
  roomTags: { value: 3.5, unit: 'mm', basis: 'cap' },
  labels: { value: 2.5, unit: 'mm', basis: 'cap' },
  openings: { value: 2, unit: 'mm', basis: 'cap' },
};

export interface HeatmapViewportSettings {
  /** Point attribute the heatmap colors by (e.g. `elevation`, or `deviation` for a surface heatmap). */
  attribute: string;
  /**
   * The surface/plane whose deviation heatmap prints; the render switches its
   * heatmap on with `gradient` and the tolerance `max(|min|, |max|)`, whatever
   * its filter state is at export time. null = print the heatmap as shown.
   */
  sourceId: string | null;
  gradient: GradientType;
  min: number;
  max: number;
}

export interface XrayViewportSettings {
  /** Density alpha as tuned on screen (`viewer.xrayDensityAlpha`). */
  alpha: number;
  /**
   * World meters per screen pixel when `alpha` was tuned. Density counts
   * points per pixel, so the print raster rescales alpha by the pixel-area
   * ratio to look like the screen at any DPI.
   */
  referenceMetersPerPixel: number;
  /**
   * Points per m² the viewer had loaded in the frame when `alpha` was tuned.
   * The print loads the area at full density (more points per pixel), so
   * alpha is also rescaled by this ratio. null = not measured.
   */
  referencePointsPerM2: number | null;
  zMin: number | null;
  zMax: number | null;
}

export interface WorldPoint2 {
  x: number;
  y: number;
}

export interface ViewportSettings {
  kind: ViewportKind;
  /** The floor shown; ignored when `followPageFloor` and the page repeats per floor. */
  floorId: string | null;
  followPageFloor: boolean;
  /** World XY at the viewport center. */
  center: WorldPoint2;
  /** Plan rotation: degrees counter-clockwise from world +Y pointing up the page. */
  headingDeg: number;
  scaleDen: number;
  /** Raster kinds: pixel density on paper. */
  dpi: number;
  /** Plan pens by layer; missing categories use DEFAULT_PLAN_LAYER_PENS. */
  layers: Partial<Record<PlanLayerCategory, PlanLayerPen>>;
  /** Plan text sizes on paper; missing categories use DEFAULT_PLAN_TEXT_SIZES. */
  textSizes: Partial<Record<PlanTextCategory, TextSize>>;
  /** K-only output for all plan linework (ignores layer colors). */
  monochrome: boolean;
  /**
   * Image kinds (density, heatmap, photo): also draw the vector plan on top
   * of the image. A view is one image layer plus optionally the plan;
   * `PLAN_VECTOR` is the plan alone.
   */
  planOverlay: boolean;
  heatmap: HeatmapViewportSettings | null;
  xray: XrayViewportSettings | null;
  /**
   * World meters per screen pixel when framed. Raster prints scale
   * fixed-size points by it so they cover the same ground on paper as on
   * screen. null = never framed.
   */
  screenMetersPerPixel: number | null;
  /** Viewer view mode captured when reframing (e.g. `PLAN_2D_SKELETON`), restored for raster renders. */
  viewMode: string | null;
  /** ISO timestamp of the last reframe; null = never framed (export warns). */
  capturedAt: string | null;
}

export interface SheetRevision {
  id: string;
  rev: string;
  date: string;
  description: string;
  by: string;
}

/** A sheet dimension's styling, as the project's dimensions have it (missing: the default). */
export const SHEET_DIMENSION_ENDS = ['tick', 'arrow', 'openArrow', 'dot', 'bar', 'none'] as const;
export type SheetDimensionEnds = (typeof SHEET_DIMENSION_ENDS)[number];
export const SHEET_DIMENSION_LINE_STYLES = ['solid', 'dashed'] as const;
export type SheetDimensionLineStyle = (typeof SHEET_DIMENSION_LINE_STYLES)[number];
export const SHEET_DIMENSION_EXTENSION_STYLES = ['dashed', 'solid', 'none'] as const;
export type SheetDimensionExtensionStyle = (typeof SHEET_DIMENSION_EXTENSION_STYLES)[number];

export interface SheetDimensionStyle {
  /** End symbol (default ticks). */
  ends?: SheetDimensionEnds;
  /** The dimension line (default solid). */
  lineStyle?: SheetDimensionLineStyle;
  /** Extension lines (default dashed). */
  extensionLines?: SheetDimensionExtensionStyle;
  /** Text size as a factor of the view's dimension text size (default 1). */
  textScale?: number;
}

/**
 * A dimension drawn on a sheet's view (only there: never a project annotation). In the
 * view's world coordinates, so it stays on the plan when the view is reframed.
 */
export interface SheetDimension extends SheetDimensionStyle {
  id: string;
  /** The measured points, world meters (plan XY). */
  a: { x: number; y: number };
  b: { x: number; y: number };
  /** Where the dimension line sits: meters from a–b along its left normal (a → b turned 90° CCW). */
  offsetM: number;
}

/** A template element moved / resized / removed on one sheet (the template itself is untouched). */
export interface SheetElementEdit {
  xMm?: number;
  yMm?: number;
  wMm?: number;
  hMm?: number;
  /** A view's frame line: false hides it, true draws it (a thin black pen when the template has none). */
  outline?: boolean;
  removed?: boolean;
}

/** What a sheet can add to its pages, and move / resize / remove of its template's. */
export const SHEET_EDITABLE_KINDS = ['viewport', 'scaleBar', 'northArrow', 'legend'] as const;
export type SheetEditableKind = (typeof SHEET_EDITABLE_KINDS)[number];

export interface SheetDoc {
  schemaVersion: typeof SHEET_DOC_SCHEMA_VERSION;
  templateRef: PrintTemplateRef;
  fieldValues: Record<string, string>;
  revisions: SheetRevision[];
  /** Floors the per-floor pages repeat for; null = every floor. */
  floorIds: string[] | null;
  viewports: Record<string, ViewportSettings>;
  /** Page instance keys left out of the export. */
  hiddenPages: string[];
  /** Elements this sheet adds to a page (views, scale bars, north arrows, legends), by page id. */
  pageElements?: Record<string, PrintTemplateElement[]>;
  /** The template's elements as this sheet moves / resizes / removes them, by element id. */
  elementEdits?: Record<string, SheetElementEdit>;
  /** Dimensions drawn on views, by viewport settings key (page instance / element id). */
  dimensions?: Record<string, SheetDimension[]>;
  /** The sheet's own text for legend rows, by legend element id, then row key (see legendRows). */
  legendLabels?: Record<string, Record<string, string>>;
}

// ─── Keys ───────────────────────────────────────────────────────────────

/** `<pageId>` or `<pageId>:<floorId>` for a per-floor instance. */
export function pageInstanceKey(pageId: string, floorId: string | null): string {
  return floorId === null ? pageId : `${pageId}:${floorId}`;
}

export function viewportSettingsKey(pageInstance: string, elementId: string): string {
  return `${pageInstance}/${elementId}`;
}

export const DEFAULT_RASTER_DPI = 300;

/** Settings for a viewport nobody framed yet: the template defaults, centered on the origin. */
export function defaultViewportSettings(kind: ViewportKind, scaleDen: number): ViewportSettings {
  return {
    kind,
    floorId: null,
    followPageFloor: true,
    center: { x: 0, y: 0 },
    headingDeg: 0,
    scaleDen,
    dpi: DEFAULT_RASTER_DPI,
    layers: {},
    textSizes: {},
    monochrome: true,
    planOverlay: false,
    heatmap: null,
    xray: null,
    screenMetersPerPixel: null,
    viewMode: null,
    capturedAt: null,
  };
}

/** Whether a view draws the vector plan (on its own or over its image). */
export function viewportShowsPlan(settings: Pick<ViewportSettings, 'kind' | 'planOverlay'>): boolean {
  return settings.kind === 'PLAN_VECTOR' || settings.planOverlay;
}

export function planLayerPen(settings: Pick<ViewportSettings, 'layers'>, category: PlanLayerCategory): PlanLayerPen {
  return settings.layers[category] ?? DEFAULT_PLAN_LAYER_PENS[category];
}

export function planTextSize(settings: Pick<ViewportSettings, 'textSizes'>, category: PlanTextCategory): TextSize {
  return settings.textSizes[category] ?? DEFAULT_PLAN_TEXT_SIZES[category];
}
